import { Build, Component, h, Prop, State, Element } from '@stencil/core';

const COMPONENT_NAME = 'dh-picture';

/**
 * Responsive image component with lazy loading, WebP support, and aspect ratio preservation.
 *
 * Supports srcSet for resolution switching, optional WebP sources via `<picture>`,
 * and media-query-based conditional loading to prevent unnecessary downloads.
 * Implements IntersectionObserver-based lazy loading with native `loading="lazy"` as fallback.
 *
 * @category Elements
 *
 * @example
 *   <dh-picture
 *     src="image-800.jpg"
 *     src-set="image-400.jpg 400w, image-800.jpg 800w, image-1200.jpg 1200w"
 *     alt="Product screenshot"
 *     aspect-ratio="16/9"
 *     lazy="true"
 *   ></dh-picture>
 */
@Component({
  tag: 'dh-picture',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhPicture {
  @Element() hostEl!: HTMLElement;

  private observer?: IntersectionObserver;

  /**
   * Image URL used as the primary fallback src for the `<img>` element.
   * @mediaType image
   */
  @Prop() src!: string;

  /**
   * Alt text for the image. Empty string is valid for decorative images.
   * @content
   */
  @Prop() alt: string = '';

  /**
   * CSS aspect ratio for the container (e.g. '16/9', '4/3', '1/1').
   * Prevents CLS by reserving vertical space before the image loads.
   */
  @Prop() aspectRatio!: string;

  /**
   * Enable lazy loading. Accepts boolean or string for SSR attribute compatibility.
   * When 'true' or true, the image loads only when near the viewport.
   * @default true
   */
  @Prop() lazy: boolean | string = true;

  /** Width attribute for the `<img>` element (e.g. 800, '800px', '100%'). */
  @Prop() width?: number | string;

  /** Height attribute for the `<img>` element (e.g. 450, '450px'). */
  @Prop() height?: number | string;

  /**
   * Source set for resolution/viewport switching (e.g. 'img.png 1x, img@2x.png 2x').
   * When using density descriptors, the 1x entry is extracted as fallback src if `src` is empty.
   * @mediaType image
   */
  @Prop() srcSet?: string;

  /**
   * WebP source set for next-gen format support (e.g. 'img.webp 1x, img@2x.webp 2x').
   * Rendered as a `<source type="image/webp">` inside a `<picture>` element.
   * @mediaType image
   */
  @Prop() webpSrcSet?: string;

  /** Browser fetch priority hint for the image resource. */
  @Prop() fetchPriority?: 'high' | 'low' | 'auto';

  /**
   * CSS object-fit value for the image.
   * @default cover
   */
  @Prop() objectFit: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' = 'cover';

  /**
   * Media query for conditional loading. When set, the image downloads only
   * on viewports matching this query. Non-matching viewports render a transparent pixel.
   * Example: '(min-width: 1024px)' loads only on desktop/tablet.
   */
  @Prop() media?: string;

  @State() inView: boolean = false;

  private get isLazy(): boolean {
    if (typeof this.lazy === 'string') {
      return this.lazy !== 'false';
    }
    return !!this.lazy;
  }

  componentWillLoad() {
    if (!Build.isBrowser) {
      this.inView = true;
      return;
    }

    if (!this.isLazy) {
      this.inView = true;
      return;
    }

    // Lazy + client (hydration): if the image was already server-rendered, keep it
    // instead of tearing it down and rebuilding it via the observer. Resetting inView
    // to false makes Stencil remove the SSR <img> and re-create it when the observer
    // fires — but the observer (rootMargin 200px) can fire immediately inside a
    // content-visibility:auto (below-fold) section during hydration, re-fetching the
    // image eagerly on first load. Native loading="lazy" on the SSR <img> already
    // defers the off-screen fetch, so keeping the server markup is safe and avoids the
    // eager load. Mirrors me-picture.
    if (this.hostEl?.querySelector('picture, img')) {
      this.inView = true;
    }
  }

  componentDidLoad() {
    if (this.isLazy && !this.inView) {
      this.observer = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting) {
            this.inView = true;
            this.observer?.disconnect();
          }
        },
        { rootMargin: '200px' },
      );
      this.observer.observe(this.hostEl);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * Parses srcSet to extract the 1x/smallest source as fallback
   * and returns the remaining entries for srcSet attribute.
   */
  private parseSrcSet(): { fallbackSrc: string; filteredSrcSet: string | undefined } {
    const fallbackSrc = this.src;

    if (!this.srcSet) {
      return { fallbackSrc, filteredSrcSet: undefined };
    }

    const parts = this.srcSet.split(',').map(s => s.trim());
    const filtered: string[] = [];
    let extracted1x: string | undefined;

    for (const part of parts) {
      const [url, descriptor = '1x'] = part.split(/\s+/);
      if (descriptor === '1x' && !extracted1x) {
        extracted1x = url;
      }
      if (descriptor !== '1x') {
        filtered.push(part);
      }
    }

    return {
      fallbackSrc: fallbackSrc || extracted1x || '',
      filteredSrcSet: filtered.length > 0 ? filtered.join(', ') : undefined,
    };
  }

  private buildImgAttrs(): Record<string, string | number | undefined> {
    const attrs: Record<string, string | number | undefined> = {};

    if (this.fetchPriority) {
      attrs.fetchpriority = this.fetchPriority;
    }
    if (this.width !== undefined) {
      attrs.width = this.width as string | number;
    }
    if (this.height !== undefined) {
      attrs.height = this.height as string | number;
    }
    if (this.isLazy) {
      attrs.loading = 'lazy';
    }

    return attrs;
  }

  private renderMediaPicture(imgAttrs: Record<string, string | number | undefined>) {
    const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    const { fallbackSrc } = this.parseSrcSet();

    return (
      <picture>
        {this.webpSrcSet && <source media={this.media} type="image/webp" srcSet={this.webpSrcSet} />}
        <source media={this.media} srcSet={this.srcSet || fallbackSrc} />
        <img src={TRANSPARENT_PIXEL} alt={this.alt} {...imgAttrs} />
      </picture>
    );
  }

  private renderStandardPicture(imgAttrs: Record<string, string | number | undefined>) {
    const { fallbackSrc, filteredSrcSet } = this.parseSrcSet();
    const needsPicture = !!this.webpSrcSet || !!this.srcSet;

    if (needsPicture) {
      return (
        <picture>
          {this.webpSrcSet && <source type="image/webp" srcSet={this.webpSrcSet} />}
          <img src={fallbackSrc} srcSet={filteredSrcSet} alt={this.alt} {...imgAttrs} />
        </picture>
      );
    }

    return <img src={fallbackSrc} alt={this.alt} {...imgAttrs} />;
  }

  render() {
    const style: Record<string, string> = {};
    if (this.aspectRatio) {
      style.aspectRatio = this.aspectRatio;
    }

    const classes: Record<string, boolean> = {
      [COMPONENT_NAME]: true,
      [`${COMPONENT_NAME}--loading`]: !this.inView,
      [`${COMPONENT_NAME}--fit-${this.objectFit}`]: this.objectFit !== 'cover',
    };

    const classList = Object.entries(classes)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(' ');

    const imgAttrs = this.buildImgAttrs();

    return (
      <div class={classList} style={style}>
        {this.inView && (
          this.media
            ? this.renderMediaPicture(imgAttrs)
            : this.renderStandardPicture(imgAttrs)
        )}
      </div>
    );
  }
}
